import React, { useContext, useRef } from 'react';

import { LicenseGMetrixTableDialog, LicenseGMetrixTableDialogHandle } from 'components/LicenseGMetrixTableDialog/LicenseGMetrixTableDialog';
import { useLicenseDefaultLookupSearchFields } from 'components/GMetrixTable/gmetrixTableSearchFields';
import { LicenseGMetrixTable } from 'components/LicenseGMetrixTable/LicenseGMetrixTable';
import GMetrixDialogConfirm from 'components/GMetrixDialogConfirm/GMetrixDialogConfirm';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { useRegisterHeaderControls } from 'components/layout';
import { PartnerApi, PartnerDetails } from 'apis/PartnerApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePartners } from 'pages/partners/context';
import { useNotification } from 'app/notifications';
import { License } from 'apis/LicenseApi';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/partners/components/LicensesTab/styles/licenses-tab.scss';

interface LicensesTabProps {
    getAllLicenses: () => Promise<void>;
    getPartnerTypeName: () => string;
    licenses: License[];
    loadingOrderableLicenses: boolean;
    partnerDetails: PartnerDetails;
    partnersCurrentLicenses: License[];
    setLicenses: React.Dispatch<React.SetStateAction<License[]>>;
    setLoadingOrderableLicenses: React.Dispatch<React.SetStateAction<boolean>>;
    setPartnersCurrentLicenses: React.Dispatch<React.SetStateAction<License[]>>;
}

export const LicensesTab = (props: LicensesTabProps) => {

    const { getAllLicenses, getPartnerTypeName, licenses, loadingOrderableLicenses, partnerDetails, partnersCurrentLicenses,
        setLicenses, setLoadingOrderableLicenses, setPartnersCurrentLicenses } = props;

    const [state, setState] = useSuperState({
        searchTerm: ''
    });
    const { searchTerm } = state;

    const { catchWithToast } = useNotification();
    const { highestRole } = useApp();
    const { formatMessage } = useIntl();
    const { permissions } = usePartners();
    const licenseDefaultLookupSearchFields = useLicenseDefaultLookupSearchFields();

    const gmetrixConfirmRef = useRef<any>(null);
    const licenseDialogRef = useRef<LicenseGMetrixTableDialogHandle>(null);

    // Dial the "Add License" CTA out to the partner detail page header.
    useRegisterHeaderControls({
        cta: {
            disabled: !permissions.Edit,
            label: (
                <div className="d-inline-flex align-items-center justify-content-center flex-fill gap-1">
                    <i className="fa-solid fa-plus" />
                    <span>
                        <FormattedMessage defaultMessage="Add License" id="add_license.e6199c74" />
                    </span>
                </div>
            ),
            onClick: () => handleAddLicenses()
        }
    });

    const handleAddLicenses = async () => {

        const result = await licenseDialogRef.current?.show();
        if (!result) return;

        await PartnerApi.AddLicensesToPartner(
            partnerDetails.ID,
            result.licenses.map(license => license.Id),
            result.licenses.map(license => license.SuppliedBy)
        );
        setLicenses(licenses.filter(license => !result.licenses.find(addedLicense => addedLicense.ID === license.ID)));
        setPartnersCurrentLicenses([...result.licenses, ...partnersCurrentLicenses]);

    };

    const removeOrderableLicense = async (dataItem: License) => {

        await PartnerApi.DeleteLicenseFromPartner(partnerDetails.ID, dataItem.Id).catch(catchWithToast(formatMessage({ id: 'failed_to_delete_license_from_partner.ba3dab19', defaultMessage: 'Failed to delete license from partner.' })));
        getAllLicenses();

    };

    return (
        <>
            <GMetrixDialogConfirm ref={gmetrixConfirmRef} />

            <LicenseGMetrixTableDialog
                highestRoleId={highestRole.partnerId}
                licenses={licenses}
                ref={licenseDialogRef}
                setLicenses={setLicenses}
            />

            <LicenseGMetrixTable
                manualLoadGridData
                manuallyRenderLicenses
                columnData={[
                    { field: 'Id', title: formatMessage({ id: 'license_id.87d2b466', defaultMessage: 'License ID' }) },
                    { field: 'Name', title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' }) },
                    { field: 'DeliverableTypeString', title: formatMessage({ id: 'deliverables.a62ac209', defaultMessage: 'Deliverables' }) },
                    {
                        field: 'SuppliedByName',
                        title: formatMessage({ id: 'supplied_by.360c7c18', defaultMessage: 'Supplied By' })
                    },
                    { field: 'LicenseTypeString', title: formatMessage({ id: 'type.b65913c1', defaultMessage: 'Type', description: 'Formulario' }) },
                    {
                        cell: ({ dataItem }) => (
                            <td>
                                <div className="d-flex justify-content-end">
                                    <GMetrixButton
                                        onClick={async () => {

                                            const result = await gmetrixConfirmRef.current.confirm(formatMessage({ id: 'license_removal.6ac46474', defaultMessage: 'License Removal' }), formatMessage({ id: 'are_you_sure_you_want_to_remove_this_lic.85e1f74b', defaultMessage: 'Are you sure you want to remove this License from this {partner}?' }, { partner: getPartnerTypeName() }));
                                            if (result) {

                                                removeOrderableLicense(dataItem);

                                            }

                                        }}
                                        className="py-2"
                                        disabled={!permissions.Edit}
                                        iconClass="fa-solid fa-trash"
                                        variant="outline-danger"
                                    >
                                    </GMetrixButton>
                                </div>
                            </td>
                        ),
                        width: 160
                    }
                ]}
                advancedSearchFields={licenseDefaultLookupSearchFields}
                allowExport={true}
                licenses={partnersCurrentLicenses}
                loadingGridData={loadingOrderableLicenses}
                searchTerm={searchTerm}
                setLicenses={setPartnersCurrentLicenses}
                setLoadingGridData={setLoadingOrderableLicenses}
                setSearchTerm={val => setState({ searchTerm: val })}
                toggleColumns={false}
            />
        </>
    );

};
